import { FormCommonProps } from "../../../common/forms/types.ts";
import { Reclamation } from "src/api/generated/erp/db/types/tables/reclamation.ts";
import { DeepPartial } from "react-hook-form";
import { FormTextField } from "../../../common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import { requireRule } from "../../../common/forms/validation.ts";
import { FormSelectField } from "../../../common/forms/fields/FormSelectField.tsx";
import { FormDateTimeField } from "../../../common/forms/fields/FormDateTimeField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { Divider } from "@mui/material";
import dayjs from "dayjs";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import {
	ReclamationEditApi,
	ReclamationEditApi_InitData,
} from "src/api/generated/erp/reclamations/api/reclamationEditApi.ts";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ReclamationFormProps extends FormCommonProps<number> {
	reclamationId: number | undefined;
	customerOrderId: number | undefined;
}

interface ReclamationFormValues extends Omit<Reclamation, "estimatedWorkMinutes"> {
	estimatedWorkHours: number | null;
}

export const ReclamationForm = (props: ReclamationFormProps) => {
	const { reclamationId, customerOrderId, onFormEdited, onCompleted } = props;
	const { defaultSiteId, appUserId } = useGlobalInitData();
	return (
		<AsyncForm<ReclamationEditApi_InitData, ReclamationFormValues, number>
			fetch={() =>
				ReclamationEditApi.getInitData({
					reclamationId: reclamationId,
					customerOrderId: customerOrderId,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submitForm}
			columns={3}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.reclamation.reclamation.form"),
				data: {
					reclamationId: reclamationId ?? null,
					customerOrderId: customerOrderId ?? null,
				},
			}}
			render={(params) => <ReclamationFormContent {...props} {...params} />}
		/>
	);

	function getDefaultValues({
		reclamation,
		customerOrder,
		reclamationCategories,
		reclamationCauses,
		reclamationHandlingMethods,
		defaultCostEventPriceListId,
	}: ReclamationEditApi_InitData): DeepPartial<ReclamationFormValues> {
		return reclamation != null ?
				toFormValues(reclamation)
			:	{
					siteId: defaultSiteId,
					plannedCompletedDate: dayjs().toISOString(),
					responsiblePersonUserId: appUserId,
					categoryId: reclamationCategories[0]?.reclamationCategoryId ?? undefined,
					causeId: reclamationCauses[0]?.reclamationCauseId ?? undefined,
					handlingMethodId:
						reclamationHandlingMethods.find((m) => m.isDefault)?.reclamationHandlingMethodId ??
						reclamationHandlingMethods[0]?.reclamationHandlingMethodId ??
						undefined,
					complainerName: customerOrder?.contactName,
					complainerPhone: customerOrder?.contactPhone,
					complainerEmail: customerOrder?.contactEmail,
					address: {
						address_1: customerOrder?.deliveryAddress_1,
						address_2: customerOrder?.deliveryAddress_2,
						city: customerOrder?.deliveryCity,
						postalCode: customerOrder?.deliveryPostalCode,
						country: customerOrder?.deliveryCountry != null ? customerOrder?.deliveryCountry : "FINLAND",
					},
					costEventPriceListId: defaultCostEventPriceListId,
				};
	}

	async function submitForm(formValues: ReclamationFormValues): Promise<number> {
		const reclamationToSave = toReclamation(formValues);
		if (reclamationToSave.reclamationId == null)
			return await ReclamationEditApi.insert({
				reclamation: reclamationToSave,
				customerOrderId: customerOrderId,
			});
		else
			return await ReclamationEditApi.update({
				reclamation: reclamationToSave,
			});
	}
};

interface ReclamationFormContentProps
	extends ReclamationFormProps, AsyncFormContentParams<ReclamationEditApi_InitData, ReclamationFormValues> {}

const ReclamationFormContent = ({
	control,
	reclamationId,
	data: {
		reclamationCategories,
		reclamationCauses,
		reclamationHandlingMethods,
		reclamationLocations,
		costEventPriceLists,
	},
}: ReclamationFormContentProps) => {
	const { userSites } = useGlobalInitData();

	const isExistingRecord = reclamationId != null;

	return (
		<>
			<FormTextField control={control} name={"title"} label={i18n.t("title")} rules={requireRule()} autoFocus />
			<FormSelectField
				control={control}
				name={"siteId"}
				label={i18n.t("site")}
				options={userSites}
				getOptionKey={(site) => site.siteId}
				getOptionLabel={(site) => site.siteName}
				disabled={isExistingRecord}
				rules={requireRule()}
			/>
			<FormAsyncUserSelectField
				control={control}
				name={"responsiblePersonUserId"}
				label={i18n.t("responsible_person")}
				rules={requireRule()}
			/>
			<FormAsyncUserSelectField control={control} name={"workerUserId"} label={i18n.t("worker")} />
			<FormTextField control={control} name={"subjectCode"} label={i18n.t("subjectCode")} />
			<FormSelectField
				control={control}
				name={"categoryId"}
				label={i18n.t("category")}
				options={reclamationCategories}
				getOptionKey={(category) => category.reclamationCategoryId}
				getOptionLabel={(category) => category.name}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"causeId"}
				label={i18n.t("cause")}
				options={reclamationCauses}
				getOptionKey={(cause) => cause.reclamationCauseId}
				getOptionLabel={(cause) => cause.name}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"handlingMethodId"}
				label={i18n.t("handling_method")}
				options={reclamationHandlingMethods}
				getOptionKey={(method) => method.reclamationHandlingMethodId}
				getOptionLabel={(method) => method.name}
			/>
			<FormSelectField
				control={control}
				name={"reclamationLocationId"}
				label={i18n.t("location")}
				options={reclamationLocations}
				getOptionKey={(location) => location.reclamationLocationId}
				getOptionLabel={(location) => location.name}
			/>
			<FormDateTimeField
				control={control}
				name={"plannedCompletedDate"}
				label={i18n.t("planned_completed_date")}
				rules={requireRule()}
			/>
			<FormDateTimeField control={control} name={"plannedStartAt"} label={i18n.t("planned_start")} />
			<FormNumberField
				control={control}
				name={"estimatedWorkHours"}
				label={i18n.t("estimated_work_hours")}
				type={"decimal"}
			/>
			<FormTextField
				control={control}
				name={"description"}
				label={i18n.t("description")}
				multiline={true}
				minRows={2}
				sx={{
					gridColumn: "1 / -1",
				}}
			/>
			<FormTextField
				control={control}
				name={"demand"}
				label={i18n.t("demand")}
				multiline={true}
				minRows={2}
				sx={{
					gridColumn: "1 / -1",
				}}
			/>
			<FormTextField
				control={control}
				name={"act"}
				label={i18n.t("act")}
				multiline={true}
				minRows={2}
				sx={{
					gridColumn: "1 / -1",
				}}
			/>
			<FormSelectField
				control={control}
				name={"costEventPriceListId"}
				label={i18n.t("price_list")}
				options={costEventPriceLists}
				getOptionKey={(priceList) => priceList.costEventPriceListId}
				getOptionLabel={(priceList) => priceList.name}
			/>
			<Divider
				textAlign={"left"}
				sx={{
					gridColumn: "1 / -1",
				}}
			>
				{i18n.t("complainer")}
			</Divider>
			<FormTextField control={control} name={"complainerName"} label={i18n.t("complainer")} />
			<FormTextField control={control} name={"complainerEmail"} label={i18n.t("complainer_email")} />
			<FormTextField control={control} name={"complainerPhone"} label={i18n.t("complainer_phone")} />
			<Divider
				textAlign={"left"}
				sx={{
					gridColumn: "1 / -1",
				}}
			>
				{i18n.t("address")}
			</Divider>
			<FormTextField control={control} name={"address.address_1"} label={i18n.t("street_address")} />
			<FormTextField control={control} name={"address.address_2"} label={i18n.t("address_2")} />
			<FormTextField control={control} name={"address.city"} label={i18n.t("city")} />
			<FormTextField control={control} name={"address.postalCode"} label={i18n.t("postal_code")} />
			<FormCountrySelectField control={control} name={"address.country"} label={i18n.t("country")} />
		</>
	);
};

function toFormValues(reclamation: Reclamation): ReclamationFormValues {
	return {
		...reclamation,
		estimatedWorkHours: minutesToHours(reclamation.estimatedWorkMinutes),
	};
}

function toReclamation(formValues: ReclamationFormValues): Reclamation {
	return {
		...formValues,
		estimatedWorkMinutes: hoursToMinutes(formValues.estimatedWorkHours),
	};
}

function minutesToHours(value: number | null | undefined): number | null {
	if (value == null) return null;
	return value / 60;
}

function hoursToMinutes(value: number | null | undefined): number | null {
	if (value == null) return null;
	return Math.round(value * 60);
}
